import type { StaffSubRoleType } from '@billx/types';

import type { AccessRoleType } from '@billx/types';

// Permission types
export type Permission =
  | 'read'
  | 'create'
  | 'update'
  | 'delete'
  | 'manage'
  | 'view_analytics'
  | 'manage_users'
  | 'manage_businesses'
  | 'manage_outlets'
  | 'manage_menu'
  | 'manage_orders'
  | 'manage_customers'
  | 'process_orders'
  | 'handle_payments'
  | 'view_reports';

// Page access levels
export type PageAccess = {
  roles: AccessRoleType[];
  staffSubRoles?: StaffSubRoleType[];
};

// Action access levels
export type ActionAccess = {
  roles: AccessRoleType[];
  staffSubRoles?: StaffSubRoleType[];
  permissions: Permission[];
};

// Role priority for hierarchy
export const ACCESS_ROLE_PRIORITY: Record<AccessRoleType, number> =
  Object.freeze({
    owner: 100,
    manager: 60,
    analyst: 40,
    staff: 20,
    unassigned: 0,
  });

// Permission labels
export const PERMISSION_LABELS: Record<Permission, string> = Object.freeze({
  read: 'View',
  create: 'Create',
  update: 'Edit',
  delete: 'Delete',
  manage: 'Manage',
  view_analytics: 'View Analytics',
  manage_users: 'Manage Users',
  manage_businesses: 'Manage Businesses',
  manage_outlets: 'Manage Outlets',
  manage_menu: 'Manage Menu',
  manage_orders: 'Manage Orders',
  manage_customers: 'Manage Customers',
  process_orders: 'Process Orders',
  handle_payments: 'Handle Payments',
  view_reports: 'View Reports',
});

// Page access definitions
export const PAGE_ACCESS: Record<string, PageAccess> = Object.freeze({
  // Dashboard pages
  dashboard: {
    roles: ['owner', 'manager', 'analyst'],
  },
  'dashboard.home': {
    roles: ['owner', 'manager', 'analyst'],
  },
  'dashboard.users': {
    roles: ['owner', 'manager'],
  },
  'dashboard.customers': {
    roles: ['owner', 'manager'],
  },
  'dashboard.businesses': {
    roles: ['owner'],
  },
  'dashboard.outlets': {
    roles: ['owner'],
  },
  'dashboard.outlet': {
    roles: ['owner', 'manager'],
  },
  'dashboard.outlet.tables': {
    roles: ['owner', 'manager'],
  },
  'dashboard.outlet.menu': {
    roles: ['owner', 'manager'],
  },
  'dashboard.orders': {
    roles: ['owner', 'manager', 'analyst'],
  },
  'dashboard.analytics': {
    roles: ['owner', 'manager', 'analyst'],
  },
  'dashboard.settings': {
    roles: ['owner', 'manager'],
  },
  'dashboard.settings.business': {
    roles: ['owner'],
  },
  'dashboard.settings.outlets': {
    roles: ['owner'],
  },
  'dashboard.settings.ordering': {
    roles: ['owner', 'manager'],
  },
  'dashboard.settings.tax': {
    roles: ['owner', 'manager'],
  },
  'dashboard.settings.notifications': {
    roles: ['owner', 'manager'],
  },
  'dashboard.settings.subscriptions': {
    roles: ['owner'],
  },

  // Staff portal (general access)
  staff: {
    roles: ['owner', 'manager', 'staff'],
  },

  // Staff home page
  'staff.home': {
    roles: ['owner', 'manager', 'staff'],
  },

  // Kitchen view
  'staff.kitchen': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['kitchen', 'supervisor'],
  },

  // Billing view
  'staff.billing': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['waiter', 'cashier', 'supervisor'],
  },

  // Delivery view
  'staff.delivery': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['delivery', 'supervisor'],
  },

  // Waiter view
  'staff.waiter': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['waiter', 'supervisor'],
  },

  // Profile view
  'staff.profile': {
    roles: ['staff', 'unassigned'],
  },

  // User view
  user: {
    roles: ['owner', 'manager', 'analyst', 'staff', 'unassigned'],
  },

  // Invitations
  invitations: {
    roles: ['owner', 'manager', 'analyst', 'staff', 'unassigned'],
  },

  // Onboarding
  onboarding: {
    roles: ['owner', 'manager', 'analyst', 'staff', 'unassigned'],
  },
});

// Action access definitions
export const ACTION_ACCESS: Record<string, ActionAccess> = Object.freeze({
  // User management
  'users.create': {
    roles: ['owner'],
    permissions: ['create', 'manage_users'],
  },
  'users.update': {
    roles: ['owner'],
    permissions: ['update', 'manage_users'],
  },
  'users.delete': {
    roles: ['owner'],
    permissions: ['delete', 'manage_users'],
  },
  'users.view': {
    roles: ['owner', 'manager'],
    permissions: ['read', 'manage_users'],
  },

  // Order management
  'orders.create': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['waiter', 'cashier', 'supervisor'],
    permissions: ['create', 'manage_orders'],
  },
  'orders.update': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['waiter', 'cashier', 'kitchen', 'supervisor'],
    permissions: ['update', 'manage_orders'],
  },
  'orders.delete': {
    roles: ['owner'],
    permissions: ['delete', 'manage_orders'],
  },
  'orders.view': {
    roles: ['owner', 'manager', 'staff'],
    permissions: ['read', 'manage_orders'],
  },
  'orders.process': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['kitchen', 'waiter', 'supervisor'],
    permissions: ['process_orders'],
  },

  // Menu management
  'menu.create': {
    roles: ['owner', 'manager'],
    permissions: ['create', 'manage_menu'],
  },
  'menu.update': {
    roles: ['owner', 'manager'],
    permissions: ['update', 'manage_menu'],
  },
  'menu.delete': {
    roles: ['owner', 'manager'],
    permissions: ['delete', 'manage_menu'],
  },
  'menu.view': {
    roles: ['owner', 'manager', 'staff'],
    permissions: ['read', 'manage_menu'],
  },

  // Customer management
  'customers.create': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['waiter', 'cashier', 'supervisor'],
    permissions: ['create', 'manage_customers'],
  },
  'customers.update': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['waiter', 'cashier', 'supervisor'],
    permissions: ['update', 'manage_customers'],
  },
  'customers.delete': {
    roles: ['owner'],
    permissions: ['delete', 'manage_customers'],
  },
  'customers.view': {
    roles: ['owner', 'manager', 'staff'],
    permissions: ['read', 'manage_customers'],
  },

  // Analytics and reports
  'analytics.view': {
    roles: ['owner', 'manager', 'analyst'],
    permissions: ['view_analytics', 'view_reports'],
  },

  // Business management
  'businesses.create': {
    roles: ['owner'],
    permissions: ['create', 'manage_businesses'],
  },
  'businesses.update': {
    roles: ['owner'],
    permissions: ['update', 'manage_businesses'],
  },
  'businesses.delete': {
    roles: ['owner'],
    permissions: ['delete', 'manage_businesses'],
  },
  'businesses.view': {
    roles: ['owner', 'manager', 'staff'],
    permissions: ['read', 'manage_businesses'],
  },

  // Outlet management
  'outlets.create': {
    roles: ['owner', 'manager'],
    permissions: ['create', 'manage_outlets'],
  },
  'outlets.update': {
    roles: ['owner', 'manager'],
    permissions: ['update', 'manage_outlets'],
  },
  'outlets.delete': {
    roles: ['owner'],
    permissions: ['delete', 'manage_outlets'],
  },
  'outlets.view': {
    roles: ['owner', 'manager', 'staff'],
    permissions: ['read', 'manage_outlets'],
  },

  // Table management
  'tables.create': {
    roles: ['owner', 'manager'],
    permissions: ['create', 'manage_outlets'],
  },
  'tables.update': {
    roles: ['owner', 'manager'],
    permissions: ['update', 'manage_outlets'],
  },
  'tables.delete': {
    roles: ['owner'],
    permissions: ['delete', 'manage_outlets'],
  },
  'tables.view': {
    roles: ['owner', 'manager', 'staff'],
    permissions: ['read', 'manage_outlets'],
  },
  'tables.update_status': {
    roles: ['owner', 'manager', 'staff'],
    staffSubRoles: ['waiter', 'supervisor'],
    permissions: ['update', 'manage_outlets'],
  },
});

// Helper functions
export const hasPageAccess = (
  role: AccessRoleType,
  subRoles: StaffSubRoleType[],
  pageKey: string
): boolean => {
  const access = PAGE_ACCESS[pageKey];
  if (!access) return false;

  // Check if user has required role
  const hasRole = access.roles.includes(role);
  if (!hasRole) return false;

  // Check staff sub-roles if specified
  if (access.staffSubRoles && role === 'staff') {
    const hasStaffSubRole = access.staffSubRoles.some((subRole) =>
      subRoles.includes(subRole)
    );
    if (!hasStaffSubRole) return false;
  }

  return true;
};

export const hasActionAccess = (
  role: AccessRoleType,
  subRoles: StaffSubRoleType[],
  actionKey: string
): boolean => {
  const access = ACTION_ACCESS[actionKey];
  if (!access) return false;

  // Check if user has required role
  const hasRole = access.roles.includes(role);
  if (!hasRole) return false;

  // Check staff sub-roles if specified
  if (access.staffSubRoles && role === 'staff') {
    const hasStaffSubRole = access.staffSubRoles.some((subRole) =>
      subRoles.includes(subRole)
    );
    if (!hasStaffSubRole) return false;
  }

  return true;
};

export const hasPermission = (
  role: AccessRoleType,
  subRoles: StaffSubRoleType[],
  permission: Permission
): boolean => {
  // Check all actions that require this permission
  for (const [actionKey, access] of Object.entries(ACTION_ACCESS)) {
    if (access.permissions.includes(permission)) {
      if (hasActionAccess(role, subRoles, actionKey)) {
        return true;
      }
    }
  }
  return false;
};

// Get all accessible pages for a user
export const getAccessiblePages = (
  role: AccessRoleType,
  subRoles: StaffSubRoleType[]
): string[] => {
  return Object.keys(PAGE_ACCESS).filter((pageKey) =>
    hasPageAccess(role, subRoles, pageKey)
  );
};

// Get all accessible actions for a user
export const getAccessibleActions = (
  role: AccessRoleType,
  subRoles: StaffSubRoleType[]
): string[] => {
  return Object.keys(ACTION_ACCESS).filter((actionKey) =>
    hasActionAccess(role, subRoles, actionKey)
  );
};

// Get required roles for a specific page
export const getPageRequiredRoles = (pageKey: string): AccessRoleType[] => {
  const access = PAGE_ACCESS[pageKey];
  return access?.roles || [];
};

// Get required roles for a specific action
export const getActionRequiredRoles = (actionKey: string): AccessRoleType[] => {
  const access = ACTION_ACCESS[actionKey];
  return access?.roles || [];
};

// Get required staff sub-roles for a specific page
export const getPageRequiredSubRoles = (
  pageKey: string
): StaffSubRoleType[] => {
  const access = PAGE_ACCESS[pageKey];
  return access?.staffSubRoles || [];
};

// Get required staff sub-roles for a specific action
export const getActionRequiredSubRoles = (
  actionKey: string
): StaffSubRoleType[] => {
  const access = ACTION_ACCESS[actionKey];
  return access?.staffSubRoles || [];
};
